"use client";

import { useLocale } from "@calcom/lib/hooks/useLocale";
import { trpc } from "@calcom/trpc/react";
import { Button } from "@calcom/ui/components/button";
import { Dialog, DialogContent, DialogFooter } from "@calcom/ui/components/dialog";
import { showToast } from "@calcom/ui/components/toast";
import { useState } from "react";
import {
  BillingProfileForm,
  type BillingProfileFormValues,
} from "~/billing-profile/components/BillingProfileForm";

const FORM_ID = "billing-profile-gate-form";

/**
 * FEAT-087: modal bloqueante post-login para capturar datos de facturación.
 *
 * Sin botón close, sin escape-to-close (`onEscapeKeyDown.preventDefault`), sin
 * overlay-click-to-close (`preventCloseOnOutsideClick`). El usuario no puede
 * navegar la app hasta completar el form (RUT validado + 7 campos obligatorios).
 *
 * Tras upsert exitoso → invalida `getStatus` para que `useBillingProfileGate`
 * vuelva a evaluar y oculte el modal.
 */
export function BillingProfileGateModal(): JSX.Element {
  const { t } = useLocale();
  const utils = trpc.useUtils();
  const [isFormValid, setIsFormValid] = useState(false);

  const upsertMutation = trpc.viewer.billingProfile.upsert.useMutation({
    onSuccess: async () => {
      await utils.viewer.billingProfile.getStatus.invalidate();
      await utils.viewer.billingProfile.get.invalidate();
      showToast(t("billing_profile_gate_saved"), "success");
    },
    onError: (error) => {
      showToast(error.message || t("onboarding_billing_save_failed"), "error");
    },
  });

  const handleSubmit = async (data: BillingProfileFormValues) => {
    await upsertMutation.mutateAsync({
      plan: "personal",
      billingDetails: data,
    });
  };

  return (
    <Dialog open>
      <DialogContent
        type="creation"
        size="md"
        preventCloseOnOutsideClick
        onEscapeKeyDown={(e) => e.preventDefault()}
        // Sin botón close: omitir la X del header — usamos creation sin DialogClose.
        title={t("billing_profile_gate_title")}
        description={t("billing_profile_gate_description")}
        enableOverflow>
        <BillingProfileForm
          formId={FORM_ID}
          onSubmit={handleSubmit}
          onValidChange={setIsFormValid}
          isSubmitting={upsertMutation.isPending}
        />
        <DialogFooter showDivider>
          <Button
            type="submit"
            form={FORM_ID}
            color="primary"
            disabled={!isFormValid || upsertMutation.isPending}
            loading={upsertMutation.isPending}>
            {t("billing_profile_gate_submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
